// Gedeelde PDF-opbouw voor ingevulde registratieformulieren.
// Gebruikt door RegistratieTab voor: PDF per formulier, PDF per week, en de afwijkingen-trend.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { weegBerekening, REG_DAGEN, REG_DAG_LABEL } from "./regFormSeed";

const paraaf = (v) => (v && v.naam ? `${v.naam}${v.ts ? ` (${new Date(v.ts).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })})` : ""}` : "—");

// Tekent één ingevuld formulier (detail) vanaf startY; geeft de nieuwe y terug.
export function drawSubmission(doc, s, tpl, startY, W) {
  let y = startY;
  if (y > 250) { doc.addPage(); y = 16; }
  doc.setFont("helvetica", "bold"); doc.setFontSize(12);
  doc.text(`${s.code || tpl.code || ""} · ${s.titel || tpl.titel || ""}`, 14, y + 6);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5);
  const meta = [s.lijn ? `Lijn: ${s.lijn}` : "", s.medewerker ? `Medewerker: ${s.medewerker}` : "", s.periode === "week" ? `week ${s.week}` : s.datum, `status: ${s.status === "afgerond" ? "AFGEROND" : "bezig"}`, s.afgerondDoor ? `afgetekend door ${s.afgerondDoor}${s.afgerondTs ? ` op ${new Date(s.afgerondTs).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}` : ""].filter(Boolean).join("   ");
  doc.text(meta, 14, y + 11);
  y += 13;
  if (s.koppeling && s.koppeling.naam) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(28, 100, 176);
    doc.text(`Gekoppelde grondstof: ${s.koppeling.naam}  ·  tracecode ${s.koppeling.code || "—"}${s.koppeling.datum ? `  ·  leverdatum ${s.koppeling.datum}` : ""}${s.koppeling.kg ? `  ·  ${Math.round(Number(s.koppeling.kg))} kg op voorraad` : ""}`, 14, y + 1);
    doc.setTextColor(0); y += 6;
  }
  const D = s.data || {};
  (tpl.secties || []).forEach((sec) => {
    let head = [], body = [];
    if (sec.type === "velden") {
      head = [[sec.titel || "Veld", "Waarde"]];
      body = (sec.velden || []).map((v) => {
        const val = (D[sec.id] || {})[v.id];
        return [v.label, v.vt === "paraaf" ? paraaf(val) : (val == null || val === "" ? "—" : String(val))];
      });
    } else if (sec.type === "checklist") {
      head = [[sec.titel || "Controlepunt", "Status", "Opmerking"]];
      body = (sec.items || []).map((it) => {
        const iv = (D[sec.id] || {})[it.id] || {};
        return [it.label, iv.status === "ok" ? "In orde" : iv.status === "niet" ? "NIET" : "—", iv.opm || ""];
      });
    } else if (sec.type === "daygrid") {
      head = [[sec.titel || "", ...REG_DAGEN.map((d) => REG_DAG_LABEL[d])]];
      body = (sec.rijen || []).map((r) => {
        const rv = (D[sec.id] || {})[r.id] || {};
        return [r.label, ...REG_DAGEN.map((d) => (r.ct === "check" ? (rv[d] === "ok" ? "✓" : "—") : (rv[d] ?? "—")))];
      });
    } else if (sec.type === "lotcodes") {
      head = [[sec.titel || "Materiaal", "Lotcode(s)", "kg"]];
      body = (sec.rijen || []).map((r) => {
        const rv = (D[sec.id] || {})[r.id] || {};
        const lots = Array.isArray(rv.lots) ? rv.lots : (rv.lot ? [{ code: rv.lot, kg: "" }] : []);
        const codes = lots.map((l) => l.code).filter(Boolean).join(", ") || (rv.naam || "—");
        const kgs = lots.map((l) => l.kg).filter(Boolean).join(", ");
        return [r.label, codes, kgs];
      });
    } else if (sec.type === "afwijkingen") {
      const rows = Array.isArray(D[sec.id]) ? D[sec.id] : [];
      head = [["Afwijking", "Tijd", "Door", "Actie"]];
      body = rows.length ? rows.map((r) => [r.omschrijving || "", r.tijd || "", r.door || "", r.actie || ""]) : [["Geen afwijkingen", "", "", ""]];
    } else if (sec.type === "weeg") {
      const calc = weegBerekening(sec, D[sec.id] || {});
      const eh = sec.eenheid || "g";
      const f1 = (x) => (x == null ? "—" : x.toLocaleString("nl-NL", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + " " + eh);
      const st = (g) => (g.min == null ? "—" : (g.gem == null ? `norm ${f1(g.doel)} ±${f1(g.marge)}` : (g.buiten ? `BUITEN MARGE (${f1(g.min)}–${f1(g.max)})` : `binnen (${f1(g.min)}–${f1(g.max)})`)));
      head = [[sec.titel || "Weeglijst", "Gewogen", "Gemiddelde", "Status"]];
      body = calc.groepen.map((g) => [g.label || "Gewichten", String(g.n), f1(g.gem), st(g)]);
      body.push([{ content: "Totaalgemiddelde" + (calc.gemengd ? " (gemengd)" : ""), styles: { fontStyle: "bold" } }, { content: String(calc.totaalN), styles: { fontStyle: "bold" } }, { content: f1(calc.totaalGem), styles: { fontStyle: "bold" } }, { content: calc.totaalMin == null ? "" : (calc.totaalBuiten ? `BUITEN MARGE (${f1(calc.totaalMin)}–${f1(calc.totaalMax)})` : `binnen (${f1(calc.totaalMin)}–${f1(calc.totaalMax)})`), styles: { fontStyle: "bold" } }]);
    }
    if (body.length) {
      autoTable(doc, { head, body, startY: y, margin: { left: 14, right: 14 }, styles: { fontSize: 8, cellPadding: 1.4 }, headStyles: { fillColor: [28, 100, 176], fontSize: 8 }, theme: "grid" });
      y = doc.lastAutoTable.finalY + 3;
    }
    if (y > 275) { doc.addPage(); y = 16; }
  });
  y += 2; doc.setDrawColor(220); doc.line(14, y, W - 14, y); y += 4;
  return y;
}

// Bouwt één PDF met één of meerdere ingevulde formulieren (per formulier of per week).
export function buildSubmissionsPdf(subs, resolveTpl, { title, subtitle, filename }) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  let y = 16;
  doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.text("Clazing Convenience Food B.V.", 14, y);
  doc.setFontSize(13); y += 7; doc.text(title, 14, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); y += 5;
  doc.text(`Afgedrukt: ${new Date().toLocaleString("nl-NL")}${subtitle ? `   ·   ${subtitle}` : ""}`, 14, y); y += 8;
  if (!subs.length) { doc.setFontSize(10); doc.text("Geen ingevulde formulieren gevonden voor deze selectie.", 14, y); }
  subs.forEach((s) => { const tpl = resolveTpl(s) || {}; y = drawSubmission(doc, s, tpl, y, W); });
  doc.save(filename);
}

// PDF-export van een afwijkingen-trend (per formuliertype).
export function buildTrendPdf(tpl, trend, { filename }) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  let y = 16;
  doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.text("Clazing Convenience Food B.V.", 14, y);
  doc.setFontSize(13); y += 7; doc.text(`Afwijkingen-trend — ${tpl.code || ""} ${tpl.titel || ""}`, 14, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); y += 5;
  doc.text(`Afgedrukt: ${new Date().toLocaleString("nl-NL")}   ·   ${trend.nForms} ingevuld(e) formulier(en)   ·   ${trend.totaalAfw} afwijking(en) totaal`, 14, y); y += 8;
  autoTable(doc, { head: [[tpl.periode === "week" ? "Week" : "Datum", "Formulieren", "Afwijkingen"]], body: trend.rows.map((r) => [r.key, String(r.formulieren), String(r.afwijkingen)]), startY: y, margin: { left: 14, right: 14 }, styles: { fontSize: 9, cellPadding: 1.6 }, headStyles: { fillColor: [28, 100, 176] }, theme: "grid" });
  y = doc.lastAutoTable.finalY + 6;
  if (trend.breakdown && trend.breakdown.length) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(10.5); doc.text("Meest voorkomende afwijkingen (controlepunt)", 14, y); y += 4;
    autoTable(doc, { head: [["Controlepunt", "Aantal keer afgeweken"]], body: trend.breakdown.map((b) => [b.label, String(b.count)]), startY: y, margin: { left: 14, right: 14 }, styles: { fontSize: 9, cellPadding: 1.6 }, headStyles: { fillColor: [190, 30, 45] }, theme: "grid" });
  }
  doc.save(filename);
}

// Telt afwijkingen in één ingevuld formulier + geeft per-controlepunt labels terug.
export function telAfwijkingen(s, tpl) {
  const num = (x) => { const n = parseFloat(String(x == null ? "" : x).replace(",", ".")); return isNaN(n) ? null : n; };
  const D = s.data || {};
  const labels = [];
  (tpl.secties || []).forEach((sec) => {
    const sv = D[sec.id] || {};
    if (sec.type === "weeg") {
      const calc = weegBerekening(sec, sv);
      calc.groepen.forEach((g) => { if (g.buiten) labels.push(`${sec.titel}${g.label && g.label !== "Gewichten" ? " – " + g.label : ""}`); });
      if (calc.totaalBuiten) labels.push(`${sec.titel} – totaalgemiddelde`);
    } else if (sec.type === "velden") {
      (sec.velden || []).forEach((v) => {
        if (v.vt !== "temp" && v.vt !== "aantal") return;
        const val = num(sv[v.id]);
        if (val == null) return;
        const min = v.min ?? v.norm; const max = v.max;
        if ((min != null && val < min) || (max != null && val > max)) labels.push(`${sec.titel} – ${v.label}`);
      });
    } else if (sec.type === "checklist") {
      (sec.items || []).forEach((it) => {
        if ((sv[it.id] || {}).status === "niet") labels.push(`${sec.titel} – ${it.label}`);
      });
    } else if (sec.type === "afwijkingen") {
      const rows = Array.isArray(D[sec.id]) ? D[sec.id] : [];
      rows.forEach((r) => { if (r && String(r.omschrijving || "").trim()) labels.push(`${sec.titel}: ${String(r.omschrijving).trim().slice(0, 60)}`); });
    }
  });
  return { count: labels.length, labels };
}
